if (!customElements.get('brp-collection-categories')) {
  customElements.define('brp-collection-categories', class extends HTMLElement {
    static cache = new Map();
    static brandCache = new Map();

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.generation = 0;
      this.container = document.getElementById('ProductGridContainer');
      this.inputs = [...this.querySelectorAll('input[data-collection-handles]')];
      this.status = this.querySelector('[data-brp-category-status]');
      this.retry = this.querySelector('[data-brp-category-retry]');
      this.brandGroup = this.querySelector('[data-brp-brand-group]');
      this.brandFieldset = this.querySelector('[data-brp-brand-fieldset]');
      this.brandOptions = this.querySelector('[data-brp-brand-options]');
      this.brandPreserved = this.querySelector('[data-brp-brand-preserved]');
      this.brandInputs = [];
      this.brandSelection = new Set();
      const options = { signal: this.abort.signal };
      this.addEventListener('input', event => event.stopPropagation(), options);
      this.addEventListener('change', event => {
        const brand = event.target.closest('input[data-brand-filter]');
        if (brand) {
          event.stopPropagation();
          this.brandSelection = this.selectedBrands();
          this.update({ saveHistory: true });
          return;
        }
        const input = event.target.closest('input[data-collection-handles]');
        if (!input) return;
        event.stopPropagation();
        if (input.checked) {
          const group = input.closest('[data-category-group]');
          const selector = input.hasAttribute('data-category-parent') ? '[data-category-child]' : '[data-category-parent]';
          group.querySelectorAll(selector).forEach(other => { other.checked = false; });
        }
        this.update({ saveHistory: true });
      }, options);
      this.querySelectorAll('[data-clear-categories]').forEach(button => button.addEventListener('click', () => {
        this.inputs.forEach(input => { input.checked = false; });
        this.update({ saveHistory: true });
      }, options));
      this.retry.addEventListener('click', () => this.refresh(), options);
      this.container.addEventListener('click', event => {
        if (event.target.closest('[data-brp-category-previous]')) this.turnPage(-1);
        if (event.target.closest('[data-brp-category-next]')) this.turnPage(1);
      }, options);
      this.observer = new MutationObserver(() => {
        if (this.container.querySelector('[data-brp-category-data]') !== this.meta) this.scheduleRefresh();
      });
      this.observer.observe(this.container, { childList: true });
      this.scheduleRefresh();
    }

    disconnectedCallback() {
      this.generation += 1;
      this.abort?.abort();
      this.requestAbort?.abort();
      this.brandAbort?.abort();
      this.contextGeneration = (this.contextGeneration || 0) + 1;
      this.observer?.disconnect();
      cancelAnimationFrame(this.refreshFrame);
      this.initialized = false;
    }

    scheduleRefresh() {
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => this.refresh());
    }

    refresh() {
      if (!this.isConnected) return;
      this.generation += 1;
      this.requestAbort?.abort();
      this.loading = null;
      this.brandAbort?.abort();
      const context = this.contextGeneration = (this.contextGeneration || 0) + 1;
      this.meta = this.container.querySelector('[data-brp-category-data]');
      if (!this.meta) return;
      this.grid = this.container.querySelector('ul#product-grid');
      this.empty = this.container.querySelector('[data-brp-category-empty]');
      this.navigation = this.container.querySelector('[data-brp-category-navigation]');
      this.nativePagination = this.container.querySelector('[data-brp-native-pagination]');
      this.pageSize = Number(this.meta.dataset.pageSize);
      this.pages = Number(this.meta.dataset.pages);
      this.total = Number(this.meta.dataset.total);
      this.complete = this.pages <= 1;
      this.records = this.readCards(this.container);
      this.sourceURL = new URL(location.href);
      ['brp_category', 'brp_subcategory', 'brp_brand', 'brp_page', 'page', 'section_id'].forEach(key => this.sourceURL.searchParams.delete(key));
      this.sourceURL.searchParams.sort();
      this.cacheKey = `${this.sourceURL.href}|${this.total}|${this.pages}|${this.pageSize}`;
      const params = new URL(location.href).searchParams;
      this.brandSelection = new Set(params.getAll('brp_brand').filter(value => value && value !== 'Not verified'));
      this.brandInputs = [];
      this.renderBrandOptions([]);
      if (this.brandFieldset) this.brandFieldset.disabled = true;
      this.preservePendingBrands();
      this.inputs.forEach(input => {
        input.disabled = false;
        input.checked = params.getAll(input.name).includes(input.value);
        if (input.checked) input.closest('[data-category-group]').open = true;
      });
      const page = Number(params.get('brp_page'));
      this.page = Number.isInteger(page) && page > 0 ? page : 1;
      const selected = this.selectedHandles();
      this.querySelector('[data-all-parts]')?.setAttribute('aria-pressed', String(selected.size === 0));
      this.initializeBrands(context).then(() => {
        if (!this.isConnected || context !== this.contextGeneration) return;
        if (this.selectedHandles().size || this.selectedBrands().size || this.page > 1) this.update({ page: this.page });
      }).catch(error => {
        if (!this.isConnected || context !== this.contextGeneration || error.name === 'AbortError') return;
        this.status.hidden = false;
        this.status.textContent = this.dataset.error;
        this.retry.hidden = false;
      });
    }

    readCards(root) {
      return [...root.querySelectorAll('[data-brp-category-card]')].map(node => ({
        node,
        id: node.dataset.productId,
        index: Number(node.dataset.resultIndex),
        collections: new Set((node.dataset.collections || '').split(',').filter(Boolean)),
        brand: node.dataset.brand || ''
      }));
    }

    selectedHandles() {
      return new Set(this.inputs.filter(input => input.checked).flatMap(input => input.dataset.collectionHandles.split(',')));
    }

    selectedBrands() {
      return this.brandInputs.length
        ? new Set(this.brandInputs.filter(input => input.checked).map(input => input.value))
        : new Set(this.brandSelection);
    }

    preservePendingBrands() {
      if (!this.brandPreserved) return;
      this.brandPreserved.replaceChildren(...[...this.brandSelection].map(brand => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'brp_brand';
        input.value = brand;
        return input;
      }));
    }

    brandCountRecords() {
      if (!this.complete) return this.brandData || [];
      const selected = this.selectedHandles();
      return this.records.filter(record => selected.size === 0 || [...selected].some(handle => record.collections.has(handle)));
    }

    renderBrandOptions(records) {
      if (!this.brandOptions) return;
      const selected = this.selectedBrands();
      const counts = new Map();
      records.forEach(record => {
        if (record.brand && record.brand !== 'Not verified') counts.set(record.brand, (counts.get(record.brand) || 0) + 1);
      });
      const names = [...new Set([...counts.keys(), ...selected])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      this.brandOptions.replaceChildren(...names.map(brand => {
        const li = document.createElement('li');
        const label = document.createElement('label');
        label.className = 'brp-filter-sidebar__option';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = 'brp_brand';
        input.value = brand;
        input.setAttribute('data-brand-filter', '');
        input.checked = selected.has(brand);
        input.disabled = !input.checked && !counts.get(brand);
        const text = document.createElement('span');
        text.textContent = `${brand} (${counts.get(brand) || 0})`;
        label.append(input, text);
        li.append(label);
        return li;
      }));
      this.brandInputs = [...this.brandOptions.querySelectorAll('[data-brand-filter]')];
      this.brandSelection = selected;
      this.brandGroup.hidden = names.length === 0;
    }

    async initializeBrands(context) {
      if (!this.brandOptions) return;
      const key = `${this.sourceURL.href}|${this.total}`;
      const cached = this.constructor.brandCache.get(key);
      if (cached) this.brandData = cached;
      else {
        const requestAbort = new AbortController();
        this.brandAbort = requestAbort;
        const signal = AbortSignal.any([requestAbort.signal, this.abort.signal]);
        const readPage = async page => {
          const url = new URL(this.sourceURL);
          url.searchParams.set('view', 'brp-brand-data');
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { signal });
          if (!response.ok) throw new Error('Manufacturer brands unavailable');
          const data = await response.json();
          if (!Array.isArray(data.products) || Number(data.page) !== page || Number(data.total) !== this.total) throw new Error('Manufacturer brands incomplete');
          return data;
        };
        const first = await readPage(1);
        const pages = Math.max(1, Number(first.pages));
        if (!Number.isInteger(pages)) throw new Error('Manufacturer brand pagination invalid');
        const records = [...first.products];
        for (let page = 2; page <= pages; page += 1) {
          const data = await readPage(page);
          if (Number(data.pages) !== Number(first.pages)) throw new Error('Manufacturer brands changed');
          records.push(...data.products);
        }
        if (context !== this.contextGeneration || signal.aborted) return;
        const unique = [...new Map(records.map(record => [String(record.id), { id: String(record.id), brand: record.brand || '' }])).values()];
        if (unique.length !== this.total) throw new Error('Manufacturer brand records missing');
        this.brandData = unique;
        this.constructor.brandCache.set(key, unique);
        if (this.constructor.brandCache.size > 3) this.constructor.brandCache.delete(this.constructor.brandCache.keys().next().value);
      }
      if (context !== this.contextGeneration) return;
      this.renderBrandOptions(this.brandCountRecords());
      this.brandFieldset.disabled = false;
      this.brandPreserved.replaceChildren();
    }

    writeURL() {
      const url = new URL(location.href);
      ['brp_category', 'brp_subcategory', 'brp_brand', 'brp_page', 'page'].forEach(key => url.searchParams.delete(key));
      this.inputs.filter(input => input.checked).forEach(input => url.searchParams.append(input.name, input.value));
      this.selectedBrands().forEach(brand => url.searchParams.append('brp_brand', brand));
      if (this.page > 1) url.searchParams.set('brp_page', String(this.page));
      const searchParams = url.searchParams.toString();
      if (url.href !== location.href) history.pushState({ searchParams }, '', url);
      if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = searchParams;
    }

    async loadAllCards() {
      if (this.complete) return;
      const cached = this.constructor.cache.get(this.cacheKey);
      if (cached) {
        this.records = cached.map(record => ({ ...record, node: record.node.cloneNode(true) }));
        this.complete = true;
        return;
      }
      if (this.loading) return this.loading;
      const sourceURL = new URL(this.sourceURL);
      const pages = this.pages;
      const currentPage = Number(this.meta.dataset.currentPage);
      const section = this.meta.dataset.section;
      const cacheKey = this.cacheKey;
      const records = [...this.records];
      const queue = Array.from({ length: pages }, (_, index) => index + 1).filter(page => page !== currentPage);
      const requestAbort = new AbortController();
      this.requestAbort = requestAbort;
      const signal = AbortSignal.any([requestAbort.signal, this.abort.signal]);
      this.loading = (async () => {
        const worker = async () => {
          while (queue.length && !signal.aborted) {
            const page = queue.shift();
            const url = new URL(sourceURL);
            url.searchParams.set('page', String(page));
            url.searchParams.set('section_id', section);
            const response = await fetch(url, { signal });
            if (!response.ok) throw new Error('Category results unavailable');
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const meta = html.querySelector('[data-brp-category-data]');
            if (!meta || Number(meta.dataset.currentPage) !== page || Number(meta.dataset.pages) !== pages) {
              throw new Error('Category results incomplete');
            }
            records.push(...this.readCards(html));
          }
        };
        await Promise.all([worker(), worker()]);
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        this.records = [...new Map(records.map(record => [record.id, record])).values()].sort((a, b) => a.index - b.index);
        if (this.records.length !== this.total) throw new Error('Category results missing products');
        this.complete = true;
        this.constructor.cache.set(cacheKey, this.records.map(record => ({ ...record, node: record.node.cloneNode(true) })));
        if (this.constructor.cache.size > 3) this.constructor.cache.delete(this.constructor.cache.keys().next().value);
      })().catch(error => {
        requestAbort.abort();
        throw error;
      }).finally(() => { this.loading = null; });
      return this.loading;
    }

    async update({ saveHistory = false, page = 1 } = {}) {
      const generation = ++this.generation;
      this.page = page;
      const selected = this.selectedHandles();
      const brands = this.selectedBrands();
      this.querySelector('[data-all-parts]')?.setAttribute('aria-pressed', String(selected.size === 0));
      this.retry.hidden = true;
      this.status.hidden = true;
      if (saveHistory) this.writeURL();
      if (!this.complete && selected.size === 0 && brands.size === 0 && page === 1) {
        this.grid?.removeAttribute('data-brp-category-busy');
        this.grid?.removeAttribute('aria-busy');
        return;
      }
      this.grid?.setAttribute('data-brp-category-busy', '');
      this.grid?.setAttribute('aria-busy', 'true');
      this.status.hidden = false;
      this.status.textContent = this.dataset.loading;
      try {
        await this.loadAllCards();
        if (generation !== this.generation || !this.isConnected) return;
        this.filtered = this.records.filter(record =>
          (selected.size === 0 || [...selected].some(handle => record.collections.has(handle))) &&
          (brands.size === 0 || brands.has(record.brand))
        );
        this.renderBrandOptions(this.brandCountRecords());
        this.render();
        this.status.hidden = true;
      } catch (error) {
        if (generation !== this.generation || error.name === 'AbortError') return;
        this.status.textContent = this.dataset.error;
        this.retry.hidden = false;
      } finally {
        if (generation === this.generation) {
          this.grid?.removeAttribute('data-brp-category-busy');
          this.grid?.removeAttribute('aria-busy');
        }
      }
    }

    render() {
      this.filteredPages = Math.max(1, Math.ceil(this.filtered.length / this.pageSize));
      this.page = Math.min(this.page, this.filteredPages);
      const start = (this.page - 1) * this.pageSize;
      if (this.grid) {
        const nodes = this.filtered.slice(start, start + this.pageSize).map(record => record.node);
        nodes.forEach(node => {
          node.classList.add('scroll-trigger--cancel');
          node.querySelectorAll('.scroll-trigger').forEach(element => element.classList.add('scroll-trigger--cancel'));
        });
        this.grid.replaceChildren(...nodes);
      }
      this.empty.hidden = this.filtered.length > 0 || !this.grid;
      this.nativePagination?.setAttribute('hidden', '');
      this.navigation.hidden = this.filteredPages <= 1;
      this.container.querySelector('[data-brp-category-previous]').disabled = this.page <= 1;
      this.container.querySelector('[data-brp-category-next]').disabled = this.page >= this.filteredPages;
      this.container.querySelector('[data-brp-category-page]').textContent = this.navigation.querySelector('nav').dataset.pageLabel
        .replace('{page}', String(this.page)).replace('{pages}', String(this.filteredPages));
      const count = (this.filtered.length === 1 ? this.dataset.countOne : this.dataset.countMany).replace('{count}', String(this.filtered.length));
      ['ProductCount', 'ProductCountDesktop'].forEach(id => {
        const element = document.getElementById(id);
        if (element) element.textContent = count;
      });
    }

    turnPage(direction) {
      this.page = Math.max(1, Math.min(this.filteredPages, this.page + direction));
      this.render();
      this.writeURL();
      this.container.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  });
}
